import produce from "immer";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchProducts,
  updateProduct,
  createProduct,
  deleteProduct,
  fetchProduct,
  listUpdateProduct,
} from "../api/product";
import { normalizeError, hasMeaningfulErrors } from "../operationFeedback";

const initialState = {
  isLoading: false,
  error: false,
  errors: null,
  data: [],
  product: {},
  count: 0,
  currentCount: 0,
  newestProductsCount: 0,
  synchedWithApiCount: 0,
};

export const initialController = {
  filters: [],
  page: 1,
  per_page: 200,
  query: "",
  sort: "desc",
  sortBy: "created_at",
  view: "all",
};

function rejectNormalized(rejectWithValue, err, operationKey) {
  return rejectWithValue(normalizeError(err, { operationKey }));
}

function isHttpFailureStatus(status) {
  const n = Number(status);
  return Number.isFinite(n) && n >= 400;
}

const GENERIC_SAFE =
  /^(En uventet feil oppstod\.|Handlingen kunne ikke fullføres\.)$/;

function rejectedErrors(action, fallbackNb) {
  const msg = action.payload?.safeMessage;
  if (typeof msg === "string" && msg.trim() && !GENERIC_SAFE.test(msg)) {
    return [msg];
  }
  return [fallbackNb];
}

export const deleteProductAsync = createAsyncThunk(
  "products/deleteProductAsync",
  async (payload, { rejectWithValue }) => {
    try {
      return await deleteProduct(payload);
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "delete_product");
    }
  }
);

export const createProductAsync = createAsyncThunk(
  "products/createProductAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await createProduct(payload);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "create_product");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "create_product");
    }
  }
);

export const updateProductAsync = createAsyncThunk(
  "products/updateProductAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateProduct(payload);
      const status = response?.response?.status ?? response?.status;
      if (
        isHttpFailureStatus(status) ||
        response?.error ||
        hasMeaningfulErrors(response?.errors)
      ) {
        return rejectNormalized(rejectWithValue, response, "update_product");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "update_product");
    }
  }
);

export const listUpdateProductAsync = createAsyncThunk(
  "products/listUpdateProductAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await listUpdateProduct(payload);
      const status = response?.response?.status ?? response?.status;
      if (
        isHttpFailureStatus(status) ||
        response?.error ||
        hasMeaningfulErrors(response?.errors)
      ) {
        return rejectNormalized(rejectWithValue, response, "list_update_product");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "list_update_product");
    }
  }
);

/** Incremented per list fetch so an older response cannot overwrite a newer search/filter (race with debounce or fast typing). */
let productsListFetchSeq = 0;

export const getProductsAsync = createAsyncThunk(
  "products/getProductsAsync",
  async (controller = initialController, { rejectWithValue }) => {
    const seq = ++productsListFetchSeq;
    try {
      const response = await fetchProducts(controller);

      if (seq !== productsListFetchSeq) {
        return rejectWithValue("stale");
      }

      const status = response?.response?.status ?? response?.status;
      if (
        isHttpFailureStatus(status) ||
        response?.error ||
        hasMeaningfulErrors(response?.errors)
      ) {
        return rejectNormalized(rejectWithValue, response, "list_products");
      }

      return response;
    } catch (err) {
      if (seq !== productsListFetchSeq) {
        return rejectWithValue("stale");
      }
      return rejectNormalized(rejectWithValue, err, "list_products");
    }
  }
);

export const getProductAsync = createAsyncThunk(
  "products/getProductAsync",
  async (id, { rejectWithValue }) => {
    try {
      const response = await fetchProduct(id);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "get_product");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "get_product");
    }
  }
);

const productSlice = createSlice({
  name: "products",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getProductsAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(getProductsAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        // Infinite scroll: append page results instead of replacing
        if (action.meta?.arg?.append) {
          const existing = new Set((state.data || []).map((d) => d.id));
          const incoming = (action.payload.data || []).filter(
            (d) => !existing.has(d.id)
          );
          state.data = [...(state.data || []), ...incoming];
        } else {
          state.data = action.payload.data;
        }
        state.count = action.payload.count;
        state.currentCount = action.payload.currentCount;
        state.newestProductsCount = action.payload.newestProductsCount;
        state.synchedWithApiCount = action.payload.synchedWithApiCount;
      })
      .addCase(getProductsAsync.rejected, (state, action) => {
        if (action.payload === "stale") {
          return;
        }
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke hente produktene.");
      })
      .addCase(updateProductAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(updateProductAsync.fulfilled, (state, action) => {
        state.product = action.payload;
        state.isLoading = false;
        state.error = false;
        state.errors = null;
      })
      .addCase(updateProductAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke oppdatere produktet.");
      })
      .addCase(listUpdateProductAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(listUpdateProductAsync.fulfilled, (state, action) => {
        state.product = action.payload;
        state.isLoading = false;
        state.error = false;
        state.errors = null;
      })
      .addCase(listUpdateProductAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke oppdatere produktene.");
      })
      .addCase(createProductAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(createProductAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          if (action.payload?.id) {
            draftState.data.push(action.payload);
          }
          draftState.isLoading = false;
          draftState.error = false;
          draftState.errors = null;
        });
      })
      .addCase(createProductAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke opprette produktet.");
      })
      .addCase(deleteProductAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(deleteProductAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          const id = action.meta?.arg;
          if (id != null && Array.isArray(draftState.data)) {
            draftState.data = draftState.data.filter((row) => row.id !== id);
          } else if (action.payload?.data) {
            draftState.data = action.payload.data;
          }
          draftState.isLoading = false;
        });
      })
      .addCase(deleteProductAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke slette produktet.");
      })
      .addCase(getProductAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(getProductAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.product = action.payload;
          draftState.isLoading = false;
          draftState.error = false;
          draftState.errors = null;
        });
      })
      .addCase(getProductAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke hente produktet.");
      });
  },
});

export const { reducer } = productSlice;

export default productSlice;

export const selectProductById = (state, productId) =>
  state.products.data.find((product) => product.id == productId);
